import React, { useState, useEffect } from 'react';
import {
  UsersIcon,
  ArrowLeftIcon,
  EyeIcon,
  CheckCircleIcon,
  XMarkIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import axios from 'axios';
import { API_BASE } from '../../lib/config';

const QuickNewUsers = ({ onBack }) => {
  const [newUsers, setNewUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('7days');

  useEffect(() => {
    fetchNewUsers();
  }, [timeFilter]);

  const fetchNewUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('admin_token'); // Try both potential keys
      const response = await axios.get(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const users = response.data.map(u => ({
        id: u.id,
        name: u.name || u.email.split('@')[0],
        email: u.email,
        user_type: u.user_type,
        prefecture: '', // Not in basic list view
        city: '',
        created_at: u.created_at,
        verification_status: u.is_verified ? 'approved' : 'not_submitted',
        last_login: u.last_active_at,
        profile_completion: 0, // Not available in basic view
        is_active: u.status === 'active'
      }));

      // Filter locally if needed, or rely on backend sort
      // Backend sorts by created_at desc
      setNewUsers(users.slice(0, 5));
    } catch (error) {
      console.error('Error fetching new users:', error);
      toast.error('新規ユーザー情報の取得に失敗しました');
      setNewUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserApproval = async (userId, action) => {
    try {
      // 実際の実装ではAPI呼び出し
      toast.success(`ユーザーを${action === 'approve' ? '承認' : '拒否'}しました`);
      fetchNewUsers(); // データを再取得
    } catch (error) {
      toast.error('操作に失敗しました');
    }
  };

  const getVerificationBadge = (status) => {
    const badges = {
      approved: { class: 'bg-green-100 text-green-800', label: '認証済み' },
      pending: { class: 'bg-yellow-100 text-yellow-800', label: '審査中' },
      not_submitted: { class: 'bg-gray-100 text-gray-800', label: '未提出' }
    };
    const badge = badges[status] || badges.not_submitted;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            ダッシュボードに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <UsersIcon className="w-8 h-8 text-blue-600 mr-3" />
            新規ユーザー確認
          </h2>
          <p className="text-gray-600 mt-1">最近登録されたユーザーの確認と承認</p>
        </div>

        {/* 期間フィルター */}
        <div className="flex items-center space-x-3">
          <CalendarIcon className="w-5 h-5 text-gray-400" />
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="1day">過去1日</option>
            <option value="7days">過去7日</option>
            <option value="30days">過去30日</option>
          </select>
        </div>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UsersIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">新規ユーザー</p>
              <p className="text-2xl font-bold text-gray-900">{newUsers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">認証済み</p>
              <p className="text-2xl font-bold text-gray-900">
                {newUsers.filter(u => u.verification_status === 'approved').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">審査中</p>
              <p className="text-2xl font-bold text-gray-900">
                {newUsers.filter(u => u.verification_status === 'pending').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <XMarkIcon className="w-6 h-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">未提出</p>
              <p className="text-2xl font-bold text-gray-900">
                {newUsers.filter(u => u.verification_status === 'not_submitted').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ユーザー一覧 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">新規ユーザー一覧</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">読み込み中...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ユーザー情報
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    タイプ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    認証状態
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    プロフィール完成度
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    登録日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {newUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 font-medium">
                            {user.name.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                          <div className="text-xs text-gray-400">
                            {user.prefecture} {user.city}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.user_type === 'job_seeker'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                        }`}>
                        {user.user_type === 'job_seeker' ? '求職者' : '事業者'}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {getVerificationBadge(user.verification_status)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${user.profile_completion}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{user.profile_completion}%</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => alert(`${user.name}の詳細を表示`)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          詳細
                        </button>
                        {user.verification_status === 'pending' && (
                          <button
                            onClick={() => handleUserApproval(user.id, 'approve')}
                            className="text-green-600 hover:text-green-900 flex items-center"
                          >
                            <CheckCircleIcon className="w-4 h-4 mr-1" />
                            承認
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickNewUsers;